// Windows 11 compatibility check (sections/lz-win11-check.liquid). Step 1: processor, RAM and drive size;
// step 2: TPM 2.0 and UEFI/Secure Boot; then one result per requirement. Everything runs in the browser.
// Processor rules follow Microsoft's "Windows 11 supported processors" lists (21H2 to 25H2): Intel and Qualcomm
// are published as series and checked with the rules below, AMD per model from lz-win11-cpus.json plus the
// series of the 25H2 list.

const OK = 'ok';
const NO = 'no';
const UNKNOWN = 'unknown';

/** Lower case without trademarks, clock speed and filler words, e.g. "core i5-8250u". */
export function cleanModel(input) {
  return String(input || '')
    .toLowerCase()
    .replace(/\(r\)|\(tm\)|®|™/g, ' ')
    .replace(/@.*$/, ' ')
    .replace(/\d+(\.\d+)?\s*ghz/g, ' ')
    .replace(/\b(intel|amd|qualcomm|cpu|processor|prozessor|processore|procesador|with|radeon|graphics|mobile|technologies)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function intel(s) {
  if (/\bultra\b/.test(s)) return OK; // Core Ultra Series 1, 2, 3
  if (/\bxeon\b/.test(s)) {
    if (/\be[357]-|\bv[2-6]\b/.test(s)) return NO; // E3/E5/E7 v2 to v6
    if (/\be-?2[1-4]\d\d[a-z]*\b/.test(s)) return OK; // E-2100 to E-2400
    if (/\bw-?1[23]\d\d[a-z]*\b|\bw-?1[01]\d{3}m\b|\bw-?2[1245]\d\d[a-z]*\b|\bw-?3[1345]\d\d[a-z]*\b|\bw[3579]-?[23]\d{3}/.test(s)) return OK; // W series, W-2400/3400
    if (/\b(bronze|silver|gold|platinum)\b.*\b\d{4}/.test(s)) return OK; // Xeon Scalable, all generations listed
    if (/\bd-?(17|18|27|28)\d\d\b/.test(s)) return OK; // D-1700/1800/2700/2800
    return UNKNOWN;
  }
  let m = s.match(/\bceleron\b\s*([gjn]?)\s*-?\s*(\d)\d{3}/);
  if (m) {
    const [, letter, first] = m;
    const series = { '': '34567', g: '456', j: '4', n: '45' }[letter] || '';
    return series.includes(first) ? OK : NO;
  }
  m = s.match(/\bpentium\b\s*(gold|silver)?\s*([gjn]?)\s*-?\s*(\d)(\d{3})([a-z]*)/);
  if (m) {
    const [, line, letter, first, rest, suffix] = m;
    if (line === 'silver' || letter === 'j' || letter === 'n') return (letter === 'j' && first === '5') || (letter === 'n' && first === '6') ? OK : NO;
    if (letter === 'g') return '567'.includes(first) ? OK : NO; // Gold G5000, G6000, G7000
    if (first === '4') return /^[uy]/.test(suffix) ? OK : NO; // Gold 4000U/4000Y
    if (first === '6' && rest.startsWith('8')) return OK; // Pentium 6800
    return '5678'.includes(first) ? OK : NO; // Gold 5000 to 8000
  }
  if (/\batom\b/.test(s)) return /\bx7\d{3}/.test(s) ? OK : NO;
  if (/\bi[3579]\s*-?\s*n\d{3}\b/.test(s)) return OK; // Core i3-N300/N305
  m = s.match(/\bi[3579]\s*-?\s*(\d{4,5})([a-z]{0,3})\d?\b/); // also 10th/11th gen names like 1035G1
  if (m) {
    const [, num, suffix] = m;
    const gen = num.startsWith('1') ? Number(num.slice(0, 2)) : Number(num[0]);
    if (gen >= 8) return OK;
    if (gen === 7 && /^xe?$/.test(suffix)) return OK; // Core 7000X series
    return NO;
  }
  m = s.match(/\bm[357]\s*-?\s*(\d)/);
  if (m) return Number(m[1]) >= 8 ? OK : NO; // 8th generation Core m
  if (/\bcore\s*[357]\s*-?\s*[a-z]?\d{3}/.test(s)) return OK; // Core 3/5/7 Series 1 and 2, Core 3 N355
  if (/\bn(9[5-9]|[12]\d\d|3\d\d)\b/.test(s)) return OK; // N90, N100, N200 series and newer N models
  if (/\bu300e?\b|^300t?$/.test(s)) return OK; // Intel Processor U300, Intel 300
  if (/\b(core\s*2|pentium\s*(4|d|dual)|celeron\s*[dme])\b/.test(s)) return NO;
  return UNKNOWN;
}

function amd(s, data) {
  const list = (key) => new Set((data?.amd?.[key]) || []);
  if (/threadripper/.test(s)) {
    const m = s.match(/(\d)\d{3}/);
    return m ? ('3579'.includes(m[1]) ? OK : NO) : UNKNOWN;
  }
  if (/epyc/.test(s)) {
    const m = s.match(/(\d{4}[a-z]?)/);
    if (!m) return UNKNOWN;
    if (list('epyc').has(m[1])) return OK;
    return /^(4\d\d[45]|7\d\d3|8\d\d4|9\d\d[45])/.test(m[1]) ? OK : UNKNOWN; // EPYC 4004/4005, 7003, 8004, 9004/9005
  }
  if (/athlon/.test(s)) {
    const m = s.match(/(gold|silver)?\s*(\d{4}[a-z]{0,2})/);
    if (!m) return NO; // Athlon 200GE, Athlon 64 and other models without a listed number
    const athlon = list('athlon');
    if (athlon.has(m[2]) || athlon.has((m[1] || '') + m[2])) return OK;
    return /^7\d{3}u/.test(m[2]) ? OK : NO; // Athlon 7000 U series
  }
  if (/ryzen/.test(s)) {
    if (/\bai\b|\bz[12]\b/.test(s)) return OK; // Ryzen AI, Z1, Z2
    const m = s.match(/\b(\d)(\d{3})([a-z]{0,3})/);
    if (m) {
      const [, first, rest, suffix] = m;
      if (list('ryzen').has(first + rest + suffix)) return OK;
      const gen = Number(first);
      if (gen >= 4) return OK; // 4000 to 9000 series
      if (gen === 3) return /^g/.test(suffix) ? NO : OK; // 3000 series except G/GE (if not listed)
      return NO; // 1000 series, 2000 models not on the list
    }
    if (/\b[12]\d{2}[a-z]{0,2}\b/.test(s)) return OK; // Ryzen 100/200 series
    return UNKNOWN;
  }
  const token = s.replace(/\s+/g, '');
  if (list('other').has(token)) return OK; // AMD 3015e, 3020e
  return UNKNOWN;
}

function qualcomm(s) {
  if (/\bx2?\s*(elite|plus)\b|\bx[12][ep]?-|snapdragon\s*x\b|^x[12][ep]?\b/.test(s)) return OK; // Snapdragon X, X2
  if (/8cx\s*gen\s*[23]|7c\+\s*gen\s*3|\bsq[23]\b/.test(s)) return OK;
  return UNKNOWN; // older Snapdragon models are only on the 22H2/23H2 lists
}

/** The model name wins over the selected manufacturer ("Ryzen 5 3600" with Intel selected). */
export function detectVendor(vendor, model) {
  const s = cleanModel(model);
  if (/ryzen|athlon|epyc|threadripper/.test(s)) return 'amd';
  if (/snapdragon|\bsq[123]\b/.test(s)) return 'qualcomm';
  if (/\b(core|celeron|pentium|xeon|atom)\b|\bi[3579]-/.test(s)) return 'intel';
  return vendor;
}

export function checkCpu(vendor, model, data) {
  const s = cleanModel(model);
  if (!s) return UNKNOWN;
  vendor = detectVendor(vendor, model);
  if (vendor === 'amd') return amd(s, data);
  if (vendor === 'qualcomm') return qualcomm(s);
  return intel(s);
}

function init(root) {
  const t = JSON.parse(root.querySelector('[data-strings]').textContent);
  const form = root.querySelector('[data-form]');
  const steps = [...root.querySelectorAll('[data-step]')];
  const result = root.querySelector('[data-result]');
  const progress = [...root.querySelectorAll('[data-progress]')];
  let data = null;
  const loadData = () => (data ||= fetch(root.dataset.cpus).then((r) => r.json()).catch(() => ({})));
  loadData();

  const field = (name) => form.elements[name];
  const vendor = field('vendor');
  const model = field('model');
  const setPlaceholder = () => { model.placeholder = t[`placeholder_${vendor.value}`] || ''; };
  vendor.addEventListener('change', setPlaceholder);
  setPlaceholder();

  let current = 0;
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const show = (index) => {
    current = index;
    steps.forEach((step, i) => { step.hidden = i !== index; });
    result.hidden = index !== steps.length;
    progress.forEach((p, i) => {
      p.classList.toggle('is-active', i === index);
      p.classList.toggle('is-done', i < index);
      if (i === index) p.setAttribute('aria-current', 'step');
      else p.removeAttribute('aria-current');
    });
    root.querySelector('.lz-w11__card').scrollIntoView({ block: 'start', behavior: calm ? 'auto' : 'smooth' });
  };
  const focusStep = (index) => steps[index]?.querySelector('[data-step-title]')?.focus({ preventScroll: true });

  const valid = (inputs) => {
    let first = null;
    for (const input of inputs) {
      const error = input.closest('.lz-field')?.querySelector('.lz-w11__error');
      const empty = !String(input.value).trim() || (input.type === 'number' && !(Number(input.value) > 0));
      input.setAttribute('aria-invalid', String(empty));
      if (error) {
        error.hidden = !empty;
        const ids = (input.getAttribute('aria-describedby') || '').split(' ').filter((id) => id && id !== error.id);
        input.setAttribute('aria-describedby', [...ids, ...(empty ? [error.id] : [])].join(' '));
      }
      if (empty && !first) first = input;
    }
    first?.focus();
    return !first;
  };

  const next = () => {
    if (!valid([model, field('ram'), field('storage')])) return;
    show(1);
    focusStep(1);
  };
  root.querySelector('[data-next]').addEventListener('click', next);
  root.querySelector('[data-back]').addEventListener('click', () => { show(0); focusStep(0); });
  root.querySelector('[data-restart]').addEventListener('click', () => {
    form.reset();
    setPlaceholder();
    show(0);
    focusStep(0);
  });

  const escape = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const fill = (text, values) => text.replace(/\{(\w+)\}/g, (_, k) => escape(values[k] ?? ''));
  const number = (value) => value.toLocaleString(document.documentElement.lang || undefined);

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    // Enter in a step 1 field submits the form: treat it as "Continue"
    if (current === 0) { next(); return; }
    const name = model.value.trim();
    const v = detectVendor(vendor.value, name);
    const ram = Number(field('ram').value);
    const storage = Number(field('storage').value);
    const tpm = form.elements.tpm.value || 'unknown';
    const sb = form.elements.secureboot.value || 'unknown';
    const cpu = checkCpu(v, name, await loadData());
    const answer = (value) => (value === 'yes' ? OK : value === 'no' ? NO : UNKNOWN);
    const rows = [
      ['cpu', cpu, cpu === OK ? t.cpu_ok : cpu === NO ? (v === 'amd' ? t.cpu_no_amd : t.cpu_no_intel) : t.cpu_unknown],
      ['ram', ram >= 4 ? OK : NO, ram >= 4 ? t.ram_ok : t.ram_no],
      ['storage', storage >= 64 ? OK : NO, storage >= 64 ? t.storage_ok : t.storage_no],
      ['tpm', answer(tpm), t[`tpm_${answer(tpm)}`]],
      ['secureboot', answer(sb), t[`sb_${answer(sb)}`]],
    ];
    const hard = rows.slice(0, 3);
    const soft = rows.slice(3);
    let verdict = 'ok';
    if (hard.some((r) => r[1] === NO)) verdict = 'no';
    else if (soft.some((r) => r[1] === NO) && cpu === OK) verdict = 'fix';
    else if (rows.some((r) => r[1] !== OK)) verdict = 'unknown';

    const values = { model: name, ram: number(ram), storage: number(storage) };
    result.querySelector('[data-verdict]').className = `lz-w11__verdict lz-w11__verdict--${verdict}`;
    result.querySelector('[data-verdict-title]').textContent = t[`${verdict}_title`];
    result.querySelector('[data-verdict-text]').textContent = t[`${verdict}_text`];
    result.querySelector('[data-rows]').innerHTML = rows.map(([key, status, text]) => `
      <li class="lz-w11__row is-${status}">
        <span class="lz-w11__mark" aria-hidden="true">${status === OK ? '✓' : status === NO ? '✕' : '?'}</span>
        <span><strong>${escape(t[`row_${key}`])}</strong><span class="visually-hidden"> (${escape(t[`status_${status}`])})</span><span>${fill(text, values)}</span></span>
      </li>`).join('');
    const shop = result.querySelector('[data-shop]');
    if (shop) shop.hidden = verdict === 'no';
    show(steps.length);
    result.querySelector('[data-verdict-title]').focus({ preventScroll: true });
  });
}

if (typeof document !== 'undefined') {
  const root = document.querySelector('[data-lz-w11]');
  if (root) init(root);
}
